import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { WatchOptions } from 'vue';
import InputRadioButton from '@/components/inputs/InputRadioButton/InputRadioButton';
import InputText from '@/components/inputs/InputText/InputText';
import { ProInclusionDTO } from '@/models/ProInclusionDTO';
import InputNumber from '@/components/inputs/InputNumber/InputNumber';
import InputSelect from '@/components/inputs/InputSelect/InputSelect';
import { ConsultationTypes, InclusionAdvice, LongConsultationReasons } from '@/models/VariousEnum';
import { SelectOptionDTO } from '@/models/SelectOptionDTO';
import { McsisRole } from '@/models/constants/McsisRole';
import { displayFitOnlyForDoctors, isAdviceModeActivatedFromSU, mustDisplaySendResultsToPharmacy } from '@/config/app.config';
import { mutations } from '@/store/store';
import InputCheckbox from '@/components/inputs/InputCheckbox/InputCheckbox';
import { isPharmacistFitOrderActivated } from '@/config/app.config';
import ParticipantService from '@/services/participant.service';

@Component({
  components: {
    InputRadioButton,
    InputText,
    InputNumber,
    InputSelect,
    InputCheckbox,
  },
})
export default class ProInclusion extends Vue {
  @Prop() public value!: ProInclusionDTO;
  @Prop({ default: false }) public readonly?: boolean;
  public proInclusion: ProInclusionDTO = {} as ProInclusionDTO;
  public pharmacistOrderFITActivated: boolean = false;
  public consultationTypes: any = ConsultationTypes;
  public longConsultationReasons: any = LongConsultationReasons;
  public longConsultationOptions: SelectOptionDTO[] = [];
  public selected = '';
  public rolePharmacist: string = McsisRole[McsisRole.Pharmacist];
  public roleDoctor: string = McsisRole[McsisRole.Doctor];
  public displayCanSeeFitResult: boolean = false;
  public advices: SelectOptionDTO[] = [];
  public isAdviceActivated = false;
  public adviceChoiceKey = '';

  private get suName() { return this.$route.params.screening; }

  public get fitOnly() {
    return displayFitOnlyForDoctors(this.$route.params.screening);
  }

  protected async mounted(): Promise<void> {
    Object.keys(InclusionAdvice).map( (advice: any) => {
      const option: SelectOptionDTO = {} as SelectOptionDTO;
      option.text = this.$i18n.t(`proInclusion.advices.${advice}`).toString();
      option.value = InclusionAdvice[advice as keyof typeof InclusionAdvice];
      this.advices.push(option);

      if (option.value === this.value.adviceChoice) {
        this.adviceChoiceKey = option.text;
      }
    });

    // Check if pharmacists make FIT orders instead of giving test directly
    this.pharmacistOrderFITActivated = isPharmacistFitOrderActivated(this.suName);
    this.isAdviceActivated = isAdviceModeActivatedFromSU(this.$route.params.screening)
      && !(await this.hasInclusionAdvice());

    this.proInclusion = this.value;

    this.selected = this.value.reasonLong;
    this.displayCanSeeFitResult = mustDisplaySendResultsToPharmacy(this.$route.params.screening);
    mutations.setIsProFit(this.proInclusion.choice === 'fit');
    this.initSelect();
  }

  protected async hasInclusionAdvice(): Promise<boolean> {
    const resp = await ParticipantService.hasParticipantInclusionAdvice(Number(this.$route.params.partId));
    return resp !== undefined ? resp : false;
  }

  protected initSelect() {
    Object.keys(LongConsultationReasons).map((reason: any) => {
      const option: SelectOptionDTO = {} as SelectOptionDTO;
      option.text = this.$i18n.t(`proInclusion.longConsultationReasons.${reason}`).toString();
      option.value = LongConsultationReasons[reason as keyof typeof LongConsultationReasons];
      this.longConsultationOptions.push(option);
    });
  }

  protected onSelectChange(selectedValue: string) {
    this.selected = selectedValue;
    this.proInclusion.reasonLong = selectedValue;
  }

  // Computed property
  get bmi(): number {
    if (this.proInclusion.weight && this.proInclusion.height) {
      if (this.proInclusion.weight > 0 && this.proInclusion.height > 0) {
        // The formula for BMI is weight in kilograms divided by height in meters squared.
        // As height is measured in centimeters, we divide by 100 to convert this to meters.
        return  Number((this.proInclusion.weight / Math.pow(this.proInclusion.height / 100, 2))
                   .toFixed(1));
      }
    }
    // default when no valid weight/height values typed
    return 0;
  }

  @Watch('value', { deep: true } as WatchOptions)
  protected onValueChanged(value: ProInclusionDTO, oldValue: ProInclusionDTO) {
    mutations.setIsProFit(this.proInclusion.choice === 'fit');
    if (oldValue !== value) {
      this.proInclusion = value;
      this.selected = this.value.reasonLong;
    }
  }
}
